import {
  APIAttachment,
  APIEntityType,
  APIFieldType,
  APIOrganizationUser,
  Named,
  Operator,
  Palette,
  ResourceType,
  SortOrder,
  WithArdoqPersistent,
} from './apiTypes';
import { ArdoqId, Entity, Persisted, Versioned } from './coreTypes';
import {
  Aggregate,
  APIReportFilters,
  ApiViewStateResponse,
  DataSourceType,
  FilterSuggestionSecondaryInformation,
  Report,
  ReportColumn,
  ReportColumnType,
} from './reportAPITypes';
import { APIScopeDataCore } from './scopeDataAPITypes';
import { FilterInterfaceFilter, FormattingFilter } from './filterApiTypes';
import type { APILoadTraversalParams } from './traversal';

export enum WidgetTypes {
  HEADER = 'header',
  TABLE = 'table',
  NUMBER = 'number',
  LINE_CHART = 'lineChart',
  PIE_CHART = 'pieChart',
  PERCENTAGE = 'percentage',
  STACKED_BAR_CHART = 'stackedBarChart',
  BAR_CHART = 'barChart',
  BUBBLE_CHART = 'bubbleChart',
  NOTE = 'note',
  GAUGE = 'gauge',
  POWER_BI_IFRAME = 'powerBiIframe',
}

export enum Periods {
  ONE_MONTH = '1m',
  THREE_MONTHS = '3m',
  SIX_MONTHS = '6m',
  ONE_YEAR = '1y',
  ALL_TIME = 'allTime',
}

export enum WidgetDataSourceTypes {
  REPORT = 'report',
  SURVEY = 'survey',
  METRICS = 'metrics',
  INLINE_TRAVERSAL = 'inline-traversal',
}

export enum NumberFormat {
  FULL = 'full',
  ABBREVIATED = 'abbreviated',
}

export enum BarChartColorOptions {
  SINGLE_COLOR = 'singleColor',
  COLOR_THEME = 'colorTheme',
}

export enum ChartOrderOptions {
  SIZE = 'size',
  LIST_FIELD = 'listField',
  FIELD_VALUE = 'fieldValue',
}

export enum NumberWidgetTrendRangeType {
  ROLLING = 'rolling',
  CALENDAR = 'calendar',
  FIXED_START = 'fixedStart',
}

export type WidgetDatasource = {
  sourceType: WidgetDataSourceTypes;
  sourceId: ArdoqId;
};

export type WidgetDataSourceEntity = Entity &
  WidgetDatasource &
  Named & {
    availableFields: DataSourceField[] | null;
    error?: Report['error'] | null;
    datasourceStrategy?: DataSourceType;
  };

export type WidgetLayout = {
  x: number;
  y: number;
  w: number;
  h: number;
  i: string;
};

type WidgetScopeData = APIScopeDataCore & {
  users: Array<Pick<APIOrganizationUser, 'name' | 'email' | '_id'>>;
  attachments: APIAttachment[];
};

export type DataSourceField = {
  field?: boolean;
  label: string;
  name: string;
  type: APIFieldType;
  custom?: boolean;
  referenceTypeOutgoing?: true;
  referenceTypeIncoming?: true;
  numberFormatOptions?: string | null;
};

export type ConditionalFormattingLimit = {
  name: string;
  above: number;
  color: string;
  id?: string; // Not to be persisted. Only for internal use as a component key
};

export type TimeLineData = Array<{
  name: string;
  label?: string;
  values: Array<{ value: number | null; _time: string }>;
  total?: number;
}>;

// for new unsaved parameters, globalFilterParameter id is a uuidv4 temp id
type DashboardFilterParameterId = ArdoqId;

// <fieldKey, globalFilterParameter id>
export type WidgetGlobalFilterBindings = Record<
  string,
  DashboardFilterParameterId
>;

// <globalFilterParameter id, warning message>
export type WidgetGlobalFilterWarnings = Record<
  DashboardFilterParameterId,
  string
>;

type BaseWidgetConfiguration = {
  _id: string;
  layout: WidgetLayout;
  name: string;
  fields: string[];
  viewType: WidgetTypes;
  columns?: ReportColumn[];
  filters?: APIReportFilters;
  // NOTE: permissions come from the API and are set for new widgets in the builder, but legacy/old widget responses may omit them, so reads must default safely
  permissions?: { canExploreDatasource: boolean; canEditWidget: boolean };
  globalFilterBindings?: WidgetGlobalFilterBindings;
  globalFilterWarnings?: WidgetGlobalFilterWarnings;
  scenarioName?: string;
  availableDataSourceFields?: DataSourceField[];
};

type CommonWidgetConfiguration = BaseWidgetConfiguration & {
  datasource: WidgetDatasource;
};

export type CategoricalChartConfiguration = CommonWidgetConfiguration & {
  aggregate?: Aggregate;
  numberFormatOptions?: string | null;
  numberSlices?: number;
  orderSelection: ChartOrderOptions;
  order: SortOrder;
  isAbbreviated?: boolean;
};

export type PieChartConfiguration = CategoricalChartConfiguration & {
  showTotal: boolean;
};

export type PercentageChartConfiguration = CommonWidgetConfiguration & {
  condition?: APIReportFilters;
};

export type GaugeChartConfiguration = CommonWidgetConfiguration & {
  condition?: APIReportFilters;
};

export type BarChartConfiguration = CategoricalChartConfiguration & {
  primaryColor?: HexColor;
  secondaryColor?: HexColor;
  colorSelection: BarChartColorOptions;
};

export type LineChartConfiguration = CommonWidgetConfiguration & {
  aggregate?: Aggregate;
  period?: Periods;
  hasDynamicYAxis: boolean;
  isAbbreviated?: boolean;
};

export type NumberWidgetConfiguration = CommonWidgetConfiguration & {
  limits?: ConditionalFormattingLimit[];
  resultLabel?: string;
  isAbbreviated?: boolean;
  aggregate?: Aggregate;
  trend?: NumberWidgetTrendConfiguration;
};

export type RollingDateRange = {
  rangeType: NumberWidgetTrendRangeType.ROLLING;
  rolling: { value: number; unit: 'day' | 'month' };
};

export type CalendarDateRange = {
  rangeType: NumberWidgetTrendRangeType.CALENDAR;
  calendar: { period: 'monthToDate' };
};

export type FixedStartDateRange = {
  rangeType: NumberWidgetTrendRangeType.FIXED_START;
  fixedStart: { date: Date };
};

export type NumberWidgetTrendRange =
  | RollingDateRange
  | CalendarDateRange
  | FixedStartDateRange;

export enum TrendColorScheme {
  CLASSIC = 'classic',
  INVERSE = 'inverse',
}

export type ExactTrendResult = {
  value: number;
  date: string;
};

export type ApproximateTrendResult = {
  value: number;
  date: string;
  daysAgo: number;
};

export enum TrendResultStatus {
  EXACT = 'exact',
  APPROXIMATE = 'approximate',
  MISSING = 'missing',
  GLOBAL_FILTERS_APPLIED = 'globalFiltersApplied',
}

export type NumberTrendColorScheme = {
  colorScheme: TrendColorScheme;
};

export type NumberWidgetTrendResult =
  | {
      resultStatus: TrendResultStatus.EXACT;
      result: ExactTrendResult;
    }
  | {
      resultStatus: TrendResultStatus.APPROXIMATE;
      result: ApproximateTrendResult;
    }
  | { resultStatus: TrendResultStatus.MISSING }
  | { resultStatus: TrendResultStatus.GLOBAL_FILTERS_APPLIED };

export type NumberWidgetTrendConfiguration = NumberWidgetTrendRange &
  NumberTrendColorScheme;

export type NumberWidgetTrend = NumberWidgetTrendConfiguration &
  NumberWidgetTrendResult;

export type StackedBarChartConfiguration = CommonWidgetConfiguration & {
  numberSlices?: number;
  period?: Periods;
  orderSelection: ChartOrderOptions;
  order: SortOrder;
};

export type TableWidgetConfiguration = CommonWidgetConfiguration & {
  aggregate?: Aggregate;
  sort: string;
  order: SortOrder;
};

export type WidgetConfiguration =
  | PieChartConfiguration
  | PercentageChartConfiguration
  | GaugeChartConfiguration
  | BarChartConfiguration
  | LineChartConfiguration
  | NumberWidgetConfiguration
  | StackedBarChartConfiguration
  | TableWidgetConfiguration;

export enum WidgetErrorCode {
  DELETED_DATASOURCE = 'DELETED_DATASOURCE',
  GENERIC_ERROR = 'GENERIC_ERROR',
  UNKNOWN_FIELD = 'UNKNOWN_FIELD',
}

type APIWidgetCommonAttributes = CommonWidgetConfiguration & {
  infoMessage?: string;
  error?: string;
  errorCode?: WidgetErrorCode;
  /**
   * Translation key under `apiErrors:<errorMessageCode>` used by the
   * frontend to render a localized version of `error`. Older API responses
   * omit this; the frontend should fall back to `error` in that case.
   */
  errorMessageCode?: string;
  /**
   * Interpolation data passed to the i18n lookup keyed by `errorMessageCode`.
   */
  errorMessageData?: Record<string, string>;
  traceId?: string;
};

export type HeaderWidgetAttributes = {
  _id: string;
  layout: WidgetLayout;
  content: string;
  viewType: WidgetTypes.HEADER;
};

export type NoteWidgetAttributes = {
  _id: string;
  layout: WidgetLayout;
  content: string;
  viewType: WidgetTypes.NOTE;
};

export type PowerBiIframeWidgetAttributes = {
  _id: string;
  layout: WidgetLayout;
  url: string;
  viewType: WidgetTypes.POWER_BI_IFRAME;
};

type HexColor = string;

export type DashboardSettings = {
  colors: Array<{ dataPoint: string; color: HexColor }>;
};

export type DrilldownMappings = Record<string, boolean | null>;

export type APIBarChartAttributes = BarChartConfiguration &
  APIWidgetCommonAttributes & {
    data: Array<{ label: string; value: number; id?: string }> | null;
    drilldownMappings: DrilldownMappings;
  };

export type APIPieChartAttributes = PieChartConfiguration &
  APIWidgetCommonAttributes & {
    data: Array<{ label: string; value: number; id?: string }> | null;
    drilldownMappings: DrilldownMappings;
  };

export type PercentageWidgetData = {
  denominator: number;
  numerator: number;
};

export type APIPercentageChartAttributes = PercentageChartConfiguration &
  APIWidgetCommonAttributes & {
    data: PercentageWidgetData | null;
    numberFormatOptions?: string | null;
  };

export type APIGaugeChartAttributes = APIPercentageChartAttributes;

export type APINumberWidgetAttributes = NumberWidgetConfiguration &
  APIWidgetCommonAttributes & {
    data: number | null;
    numberFormatOptions?: string | null;
    trend?: NumberWidgetTrend;
  };

export type APITableWidgetAttributes = TableWidgetConfiguration &
  APIWidgetCommonAttributes & {
    data: Array<Record<string, any>> | null;
    tableFields: Array<{
      name: string;
      label: string;
      type: ReportColumnType;
    }>;
    scopeData: WidgetScopeData | null;
    aggregates: Record<string, Record<Aggregate, number>>;
    totalNumberOfRows: number;
  };

export type APILineChartAttributes = LineChartConfiguration &
  APIWidgetCommonAttributes & {
    data: TimeLineData;
    numberFormatOptions?: string | null;
  };

export type APIStackedBarChartAttributes = StackedBarChartConfiguration &
  APIWidgetCommonAttributes & {
    drilldownMappings: DrilldownMappings;
    data: TimeLineData | null;
  };

export type APIBubbleChartDataPoint = {
  cid: ArdoqId;
  uniqueId: string;
  label: string;
  x: number;
  y: number;
  radius: number;
  /** The component's type id; the bubble's CSS class is derived from it on the frontend. */
  typeId: ArdoqId;
  isContext: boolean;
  fill?: string;
  stroke?: string;
};

export type BubbleChartCustomQuadrantSettings = {
  xMinimum: number;
  xMaximum: number;
  yMinimum: number;
  yMaximum: number;
  labels: [string, string, string, string];
};

export type BubbleChartBackgroundMode = 'none' | 'grid' | 'time' | 'custom';

export type BubbleChartRenderSettings = {
  background?: BubbleChartBackgroundMode;
  bubbleAreaScaleFactor?: number;
  customQuadrantSettings?: BubbleChartCustomQuadrantSettings;
  showBubblesWithZeroValue?: boolean;
  xAxisTitle?: string;
  yAxisTitle?: string;
};

export type BubbleChartWidgetConfiguration = BaseWidgetConfiguration &
  BubbleChartRenderSettings & {
    datasource: BubbleChartWidgetDatasource;
    /** fields = [xFieldName, yFieldName] or [xFieldName, yFieldName, radiusFieldName] — order is significant. */
    accessWorkspaceIds?: ArdoqId[];
    selectedComponentTypeIds?: string[];
    selectedComponentIds?: string[];
    conditionalFormatting?: (FormattingFilter | FilterInterfaceFilter)[] | null;
    /** Pre-resolved conditional formatting colors per component ID, captured
     *  at widget creation time. The BE's rule evaluation can't resolve all FE
     *  filter types (e.g. type-name comparisons), so these colors are used as
     *  a fallback on the dashboard widget when the BE returns no fill/stroke. */
    resolvedComponentColors?: Record<string, string>;
    /** Pre-resolved labels per component ID, captured at widget creation time.
     *  Includes label formatting rules (field values appended to the name)
     *  that the BE can't evaluate. Falls back to the BE-provided label. */
    resolvedLabels?: Record<string, string>;
  };

export type APIBubbleChartAttributes = BubbleChartWidgetConfiguration &
  Omit<APIWidgetCommonAttributes, 'datasource'> & {
    data: APIBubbleChartDataPoint[] | null;
  };

export type APIChartWidgetAttributes =
  | APITableWidgetAttributes
  | APILineChartAttributes
  | APIPieChartAttributes
  | APIPercentageChartAttributes
  | APIGaugeChartAttributes
  | APIStackedBarChartAttributes
  | APINumberWidgetAttributes
  | APIBarChartAttributes
  | APIBubbleChartAttributes;

export type AnyWidgetAttributes =
  | HeaderWidgetAttributes
  | APIChartWidgetAttributes
  | NoteWidgetAttributes
  | PowerBiIframeWidgetAttributes;

/**
 * Datasource of a bubble chart widget. For the `inline-traversal` sourceType it carries the
 * traversal execution payload inline. The backend re-runs the inline payload on every render; no
 * traversal entity is created.
 */
export type BubbleChartWidgetDatasource = {
  sourceType: WidgetDataSourceTypes.INLINE_TRAVERSAL;
  traversalDefinition: APILoadTraversalParams;
};

export type AnyWidgetDatasource =
  | WidgetDatasource
  | BubbleChartWidgetDatasource;

/**
 * A bubble chart widget as sent on the create/update path, before the backend assigns an
 * `_id`/layout key.
 */
export type UnsavedBubbleChartWidgetAttributes = Omit<
  BubbleChartWidgetConfiguration,
  '_id' | 'layout'
> & {
  _id?: string;
  layout: Omit<WidgetLayout, 'i'>;
};

/**
 * Any widget shape accepted by dashboard create/update. This is the persisted-config shape, not
 * the render shape (`AnyWidgetAttributes`) — computed fields like `data`/`scopeData` are not part
 * of it. Mirrors the widget union of `PreparedDashboard` in `@ardoq/api`.
 */
export type DashboardWidgetToSave =
  | WidgetConfiguration
  | UnsavedBubbleChartWidgetAttributes
  | HeaderWidgetAttributes
  | NoteWidgetAttributes
  | PowerBiIframeWidgetAttributes;

export type SupportedDashboardFilterReferenceType =
  | ReportColumnType.REFERENCE_TYPE_INCOMING
  | ReportColumnType.REFERENCE_TYPE_OUTGOING;

export type SupportedDashboardFilterFieldType =
  | APIFieldType.NUMBER
  | APIFieldType.CHECKBOX
  | APIFieldType.LIST
  | APIFieldType.SELECT_MULTIPLE_LIST
  | APIFieldType.USER
  | APIFieldType.TEXT
  | APIFieldType.DATE_TIME
  | APIFieldType.DATE_ONLY;

export type SupportedDashboardFilterType =
  | SupportedDashboardFilterFieldType
  | SupportedDashboardFilterReferenceType;

export type APILinkedDashboardFilterParameter = {
  id: DashboardFilterParameterId;
  name: string;
  type: SupportedDashboardFilterType;
  fieldApiName: string; // field key
};

export type APIInitialDashboardFilterParameter = {
  id: DashboardFilterParameterId;
  name: string;
  type: undefined; // This is when the parameter isn't linked to any widget yet
  fieldApiName: undefined;
};

export type APIDashboardFilterParameter =
  | APILinkedDashboardFilterParameter
  | APIInitialDashboardFilterParameter;

export type APIDashboardFilterExpression = {
  operator: Operator;
  value: string | null | boolean | number;
  label?: string;
};

export type DashboardFilterValues = Record<
  DashboardFilterParameterId,
  APIDashboardFilterExpression[]
>;

export type APIDashboardAttributes = {
  ardoq: { ['entity-type']: APIEntityType.DASHBOARD };
  _id: ArdoqId;
  name: string;
  description?: string;
  widgets: Array<AnyWidgetAttributes>;
  paletteColors?: HexColor[] | null;
  settings?: DashboardSettings;
  periodStart?: Date;
  periodEnd?: Date;
  error?: string;
  lastModifiedByName?: string;
  lastUpdated?: string;
  createdByName?: string;
  createdBy: ArdoqId;
  lastModifiedBy: ArdoqId;
  _version: number;
  globalFilterParameters: APIDashboardFilterParameter[];
  globalFilterValues?: DashboardFilterValues;
  scenarioBranchId?: ArdoqId;
  scenarioBranchIds: ArdoqId[];
} & WithArdoqPersistent;

export type APIDashboardColorTheme = Persisted<
  Palette &
    Versioned & {
      isSelected: boolean;
      label: string;
      description?: string;
      isBuiltIn?: boolean;
    }
>;

export type APIDashboardViewStateResponse = ApiViewStateResponse & {
  entityType: ResourceType.DASHBOARD;
};

export type DashboardViewState = APIDashboardViewStateResponse & {
  state: {
    dashboardFilterValues: DashboardFilterValues;
  };
};

type APIDashboardFilterSuggestionBase = {
  label: string;
  value: string | ArdoqId;
  secondaryInformation?: FilterSuggestionSecondaryInformation[];
};

export type APIFlatDashboardFilterSuggestion = APIDashboardFilterSuggestionBase;

export type APIHierarchicalDashboardFilterSuggestion =
  APIDashboardFilterSuggestionBase & {
    parentValue: string | null;
    workspace: { id: string; name: string };
    isSelectable: boolean;
  };

export type APIDashboardFilterSuggestion =
  | APIFlatDashboardFilterSuggestion
  | APIHierarchicalDashboardFilterSuggestion;

export type APIDashboardFilterSuggestionsResponse<
  T extends APIDashboardFilterSuggestion = APIDashboardFilterSuggestion,
> = {
  results: T[];
  widgetFilteredReports?: string[];
};
